/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { StringUtils } from "./string-utils";
export class RoutingUtilities {
    static getQueryParameters(queryString) {
        if (!queryString) {
            return {};
        }
        if (queryString.startsWith('?')) {
            queryString = queryString.substring(1);
        }
        const pairs = queryString.split('&').filter(p => p);
        const params = {};
        pairs.forEach(pair => {
            const pairs = pair.split('=');
            if ((pairs === null || pairs === void 0 ? void 0 : pairs.length) < 2) {
                return;
            }
            params[pairs[0]] = decodeURIComponent(pairs[1]);
        });
        return params;
    }
    static getRelativeUrlIfSameOrigin(window, urlStr) {
        if (!urlStr) {
            return urlStr;
        }
        const trimmedCurrentPathname = StringUtils.trimChar(window.location.pathname, '/');
        if (urlStr.indexOf('://')) {
            try {
                const url = new URL(urlStr);
                if (url && url.origin === window.location.origin) {
                    const urlPathname = StringUtils.trimChar(url.pathname, '/');
                    if (trimmedCurrentPathname === urlPathname) {
                        return `${url.pathname}${url.search}`;
                    }
                }
            }
            catch (e) {
                return urlStr;
            }
        }
        else if (!urlStr.startsWith('/')) {
            return `/${urlStr}`;
        }
        return urlStr;
    }
    static routeStartsWith(source, target) {
        if (!source && !target) {
            return true;
        }
        if (!source || !target) {
            return false;
        }
        const sanitizedSourceUrl = RoutingUtilities.sanitizeAndParseUrl(source);
        const sanitizedTargetUrl = RoutingUtilities.sanitizeAndParseUrl(target);
        if (!sanitizedSourceUrl || !sanitizedTargetUrl) {
            return false;
        }
        if (sanitizedSourceUrl.origin !== sanitizedTargetUrl.origin) {
            return false;
        }
        const sourceSegments = sanitizedSourceUrl.pathname.split('/');
        const targetSegments = sanitizedTargetUrl.pathname.split('/');
        if (sourceSegments.length < targetSegments.length) {
            return false;
        }
        const nonMatchingSegmentIndex = targetSegments.findIndex((targetSegment, index) => {
            const sourceSegment = sourceSegments[index];
            if (sourceSegment !== targetSegment) {
                return true;
            }
            return false;
        });
        return nonMatchingSegmentIndex < 0;
    }
    static routeEqual(source, target, excludeQueryParameters) {
        if (!source && !target) {
            return true;
        }
        if (!source || !target) {
            return false;
        }
        const sanitizedSourceUrl = RoutingUtilities.sanitizeAndParseUrl(source);
        const sanitizedTargetUrl = RoutingUtilities.sanitizeAndParseUrl(target);
        if (!sanitizedSourceUrl || !sanitizedTargetUrl) {
            return false;
        }
        return sanitizedSourceUrl.origin === sanitizedTargetUrl.origin &&
            sanitizedSourceUrl.pathname === sanitizedTargetUrl.pathname &&
            (!!excludeQueryParameters || sanitizedSourceUrl.search === sanitizedTargetUrl.search);
    }
    static getPathName(url) {
        var _a;
        return (_a = RoutingUtilities.sanitizeAndParseUrl(url)) === null || _a === void 0 ? void 0 : _a.pathname;
    }
    static sanitizeAndParseUrl(url) {
        try {
            if (!url) {
                return undefined;
            }
            if (url.indexOf('://') < 0) {
                if (!url.startsWith('/')) {
                    url = `/${url}`;
                }
                return new URL(url, window.origin);
            }
            return new URL(url);
        }
        catch (e) {
            return undefined;
        }
    }
}
RoutingUtilities.STUB_ORIGIN = 'https://stub.origin.dell.com';
//# sourceMappingURL=data:application/json;base64,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